/* import __COLOCATED_TEMPLATE__ from './smart-suggestions-section.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type { SuggestionGroup } from './types';

const ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {
  knowledge_base_insufficient_content:
    'messenger.sales.smart-suggestions.error-codes.knowledge-base-insufficient-content',
  all_questions_filtered: 'messenger.sales.smart-suggestions.error-codes.all-questions-filtered',
  llm_no_suitable_suggestions:
    'messenger.sales.smart-suggestions.error-codes.llm-no-suitable-suggestions',
  llm_service_temporary_failure:
    'messenger.sales.smart-suggestions.error-codes.llm-service-temporary-failure',
  input_validation_failed: 'messenger.sales.smart-suggestions.error-codes.input-validation-failed',
  internal_unexpected_error:
    'messenger.sales.smart-suggestions.error-codes.internal-unexpected-error',
  generation_failed: 'messenger.sales.smart-suggestions.error-codes.generation-failed',
  scrape_no_content: 'messenger.sales.smart-suggestions.error-codes.scrape-no-content',
  scrape_failed: 'messenger.sales.smart-suggestions.error-codes.scrape-failed',
};

interface Suggestion {
  id: number;
  text: string;
  display_priority: number;
}

interface GenerationJob {
  job_id: number;
  status: string;
  website_url: string;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  error_code: string | null;
}

interface Args {
  smartSuggestionsEnabled: boolean;
  suggestions: Suggestion[];
  showAllSuggestions: boolean;
  isGeneratingSuggestions: boolean;
  isEditingWebsiteUrl: boolean;
  generationJob: GenerationJob | null;
  websiteUrl: string;
  onToggle: () => void;
  onToggleShowAll: () => void;
  onUpdateWebsiteUrl: (event: Event) => void;
  onGenerate: () => void;
  onStartEditingUrl: () => void;
  onCancelEditingUrl: () => void;
  suggestionGroups: SuggestionGroup[];
  onRemoveSuggestion: (groupUuid: string) => void;
  failedGenerationUrl: string;
  failedGenerationErrorMessage: string;
  failedGenerationErrorCode: string;
  isGenerationErrorDismissed: boolean;
  onDismissGenerationError: () => void;
  onRetryFailedGeneration: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

export default class SmartSuggestionsSection extends Component<Signature> {
  @service declare intl: IntlService;

  get translatedErrorMessage(): string | null {
    let errorCode = this.args.failedGenerationErrorCode;
    if (errorCode) {
      let key = ERROR_CODE_TRANSLATION_KEYS[errorCode];
      if (key && this.intl.exists(key)) {
        return this.intl.t(key);
      }
    }
    return this.args.failedGenerationErrorMessage || null;
  }

  get hasGroupData(): boolean {
    return this.args.suggestionGroups.length > 0;
  }

  get displayedGroups(): SuggestionGroup[] {
    if (this.args.showAllSuggestions) {
      return this.args.suggestionGroups;
    }
    return this.args.suggestionGroups.slice(0, 3);
  }

  get hasMoreSuggestions(): boolean {
    return this.args.suggestionGroups.length > 3;
  }

  get remainingCount(): number {
    return this.args.suggestionGroups.length - 3;
  }

  get hasNotEnoughSuggestions(): boolean {
    return this.args.suggestionGroups.length > 0 && this.args.suggestionGroups.length < 3;
  }

  get hasGenerationError(): boolean {
    return Boolean(this.args.failedGenerationUrl && !this.args.isGenerationErrorDismissed);
  }

  get isErrorDismissible(): boolean {
    return this.hasGroupData;
  }

  get failedDisplayUrl(): string {
    return this.args.failedGenerationUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  }

  get hasCompletedUrl(): boolean {
    let job = this.args.generationJob;
    return Boolean(
      job && (job.status === 'completed' || job.status === 'failed') && job.website_url,
    );
  }

  get showUrlDisplay(): boolean {
    return (
      this.hasCompletedUrl && !this.args.isEditingWebsiteUrl && !this.args.isGeneratingSuggestions
    );
  }

  get showUrlInput(): boolean {
    if (this.args.isGeneratingSuggestions) {
      return false;
    }
    return !this.hasCompletedUrl || this.args.isEditingWebsiteUrl;
  }

  get showCancelButton(): boolean {
    return this.args.isEditingWebsiteUrl && this.hasCompletedUrl;
  }

  get displayUrl(): string {
    return this.args.websiteUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  }

  get isGenerateDisabled(): boolean {
    return !this.args.websiteUrl.trim() || this.args.isGeneratingSuggestions;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Sales::SmartSuggestionsSection': typeof SmartSuggestionsSection;
  }
}
